"use client";

import { useEffect, useRef, useState } from "react";

const SCRIPT_ID = "neverbounce-widget-script";

// NeverBounce debounces validation after the user stops typing, controlled by its
// `inputLatency` setting (widget default: 600ms). When that time is reached, the widget fires
// as soon as the partial text looks like a possible email
const INPUT_LATENCY_MS = 1000;

declare global {
  interface Window {
    _NBSettings?: {
      apiKey: string;
      displayPoweredBy: boolean;
      softRejectMessage: string;
      acceptedStatusCodes: number[];
      inputLatency: number;
    };
    _nb?: {
      remoteActivate?: () => void;
    };
  }
}

/**
 * Parses a comma-separated string of NeverBounce status codes into a number array.
 * Defaults to [0] (Valid only) if the input is absent or contains no valid integers.
 *
 * NeverBounce status codes:
 *   0 = Valid, 1 = Invalid, 2 = Disposable, 3 = Catch-all/Unknown, 4 = Accept-all/Risky
 */
export function parseAcceptedCodes(raw: string | undefined): number[] {
  if (!raw) return [0];
  const parsed = raw
    .split(",")
    .map((s) => parseInt(s.trim(), 10))
    .filter((n) => !isNaN(n));
  return parsed.length > 0 ? parsed : [0];
}

export interface NeverBounceResult {
  /** Whether NeverBounce is actively controlling validation (script loaded & running). */
  active: boolean;
  /** Whether NeverBounce has accepted the current email. */
  accepted: boolean;
  /** Error message when NeverBounce explicitly rejects the email, null otherwise. */
  error: string | null;
}

/**
 * Injects the NeverBounce email validation widget when the email question is active.
 *
 * Returns accepted=false until NeverBounce fires nb:result with an accepted code.
 * Returns an error message when NeverBounce explicitly rejects the email.
 *
 * @param enabled - Pass `true` only when the email question is active.
 * @param softRejectMessage - Translated message shown when NeverBounce cannot verify the email.
 * @returns NeverBounceResult with accepted state and optional error message.
 */
export function useNeverBounce(
  enabled: boolean,
  softRejectMessage: string,
): NeverBounceResult {
  const nbEnabled = process.env.NEXT_PUBLIC_NEVERBOUNCE_ENABLED === "true";
  const apiKey = process.env.NEXT_PUBLIC_NEVERBOUNCE_API_KEY ?? "";
  const rawCodes = process.env.NEXT_PUBLIC_NEVERBOUNCE_ACCEPTED_CODES ?? "";

  // If the field already has a value on mount (back-navigation), start accepted.
  // The email was already validated on the original pass — no need to re-check.
  // NB will naturally re-validate if the user edits the field (fires on blur).
  const inputHasValue = () => {
    const el = document.querySelector(
      ".text-input-field input",
    ) as HTMLInputElement | null;
    return !!el?.value;
  };

  const [isAccepted, setIsAccepted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const codesRef = useRef<number[]>([0]);
  const rejectMsgRef = useRef(softRejectMessage);
  rejectMsgRef.current = softRejectMessage;

  useEffect(() => {
    if (!enabled || !nbEnabled || !apiKey) return;

    const acceptedCodes = parseAcceptedCodes(rawCodes);
    codesRef.current = acceptedCodes;

    // If field is pre-filled (back-navigation), start accepted immediately.
    // User already passed NB on the first pass — don't waste a check.
    const prefilled = inputHasValue();
    if (prefilled) {
      setIsAccepted(true);
      setError(null);
    }

    window._NBSettings = {
      apiKey,
      displayPoweredBy: false,
      softRejectMessage,
      acceptedStatusCodes: acceptedCodes,
      inputLatency: INPUT_LATENCY_MS,
    };

    const body = document.querySelector("body")!;

    const handleLoading = () => {
      setIsAccepted(false);
      setError(null);
    };

    const handleResult = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (
        detail?.result &&
        typeof detail.result.getNumericCode === "function"
      ) {
        const code: number = detail.result.getNumericCode();
        const accepted = codesRef.current.includes(code);
        setIsAccepted(accepted);
        setError(accepted ? null : rejectMsgRef.current);
      }
    };

    const handleSoftResult = () => {
      setIsAccepted(false);
      setError(rejectMsgRef.current);
    };

    const handleClear = (e: Event) => {
      // NeverBounce dispatches nb:clear when the API rejects the verification,
      // including IP rate-limit throttling. When throttled, let the user proceed
      // (accepted) without showing an error message. Otherwise this behaves as a
      // normal clear: not yet validated, with no error.
      const result = (e as CustomEvent).detail?.result;
      const throttled = !!(
        result &&
        typeof result.isThrottled === "function" &&
        result.isThrottled()
      );
      setIsAccepted(throttled);
      setError(null);
    };

    body.addEventListener("nb:loading", handleLoading);
    body.addEventListener("nb:result", handleResult);
    body.addEventListener("nb:soft-result", handleSoftResult);
    body.addEventListener("nb:clear", handleClear);

    // Strip inline styles NeverBounce injects on the input and its wrapper div.
    // This lets our CSS control presentation without needing !important overrides.
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (
          mutation.type === "attributes" &&
          mutation.attributeName === "style"
        ) {
          (mutation.target as HTMLElement).removeAttribute("style");
        }
      }
    });

    const input = document.querySelector(".text-input-field input");
    if (input) {
      observer.observe(input, { attributes: true, attributeFilter: ["style"] });
      // Also observe the NB wrapper div if it already exists
      const nbWrapper = input.closest("[id^='nb-field-']");
      if (nbWrapper) {
        observer.observe(nbWrapper, {
          attributes: true,
          attributeFilter: ["style"],
        });
      }
    }

    // NB may inject its wrapper after the script loads — watch for it via childList
    const fieldContainer = input?.parentElement;
    const childObserver = fieldContainer
      ? new MutationObserver((_, obs) => {
          const wrapper = fieldContainer.querySelector("[id^='nb-field-']");
          if (wrapper) {
            wrapper.removeAttribute("style");
            observer.observe(wrapper, {
              attributes: true,
              attributeFilter: ["style"],
            });
            obs.disconnect();
          }
        })
      : null;
    childObserver?.observe(fieldContainer!, { childList: true, subtree: true });

    // Always inject a fresh script. Using a cache-buster query param forces the
    // browser to re-execute the NB script so it binds to the current DOM element.
    // Without this, navigating away and back leaves NB bound to the old (removed) input.
    document.getElementById(SCRIPT_ID)?.remove();
    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.src = `https://cdn.neverbounce.com/widget/dist/NeverBounce.js?_=${Date.now()}`;
    script.async = true;
    document.head.appendChild(script);

    return () => {
      observer.disconnect();
      childObserver?.disconnect();
      body.removeEventListener("nb:loading", handleLoading);
      body.removeEventListener("nb:result", handleResult);
      body.removeEventListener("nb:soft-result", handleSoftResult);
      body.removeEventListener("nb:clear", handleClear);
      document.getElementById(SCRIPT_ID)?.remove();
      delete window._NBSettings;
      delete window._nb;
    };
  }, [enabled, nbEnabled, apiKey, rawCodes, softRejectMessage]);

  if (!enabled || !nbEnabled)
    return { active: false, accepted: true, error: null };
  if (!apiKey) {
    console.error(
      "[useNeverBounce] NEXT_PUBLIC_NEVERBOUNCE_API_KEY is not set — falling back to field validation.",
    );
    return { active: false, accepted: true, error: null };
  }
  return { active: true, accepted: isAccepted, error };
}
